import React, { useState } from "react";
import { Sparkles, Plus, X } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { aiApi } from "@/lib/api";

/**
 * AI helpers strip for the New Task dialog (and reusable elsewhere).
 *
 * Two affordances:
 *   1. ESTIMATE — fetches a three-point duration estimate for the current title
 *      and calls onEstimateApplied({min, likely, max, confidence, rationale}).
 *      Caller decides whether to pre-fill the duration input.
 *
 *   2. SUGGEST SUBTASKS — fetches 3-6 likely subtasks; each can be inserted via
 *      onSubtaskAdded({title, duration_min}). Caller appends to its subtask list.
 *
 * Designed to be self-contained: no internal state leaks back to the parent
 * apart from the two callbacks. Disabled when title is blank.
 */
export default function AiHelpers({
  projectId,
  title,
  roomId,
  parentDurationMin,
  onEstimateApplied,
  onSubtaskAdded,
}) {
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [subs, setSubs] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [insertedTitles, setInsertedTitles] = useState(() => new Set());

  const cleanTitle = (title || "").trim();
  const disabled = !cleanTitle || !projectId;

  const runEstimate = async () => {
    if (disabled || estimating) return;
    setEstimating(true);
    try {
      const r = await aiApi.estimate({ projectId, title: cleanTitle, roomId });
      setEstimate(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Estimator failed.");
    } finally {
      setEstimating(false);
    }
  };

  const applyEstimate = () => {
    if (!estimate) return;
    onEstimateApplied && onEstimateApplied(estimate);
    toast.success(`Applied AI estimate · ${estimate.likely_minutes}m`);
  };

  const runSuggest = async () => {
    if (disabled || suggesting) return;
    setSuggesting(true);
    try {
      const r = await aiApi.suggestSubtasks({
        projectId,
        title: cleanTitle,
        roomId,
        parentDurationMin,
      });
      setSubs(r.subtasks || []);
      if (!(r.subtasks || []).length) {
        toast.info("No subtasks suggested for this title.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subtask suggester failed.");
    } finally {
      setSuggesting(false);
    }
  };

  const insertSub = (s) => {
    if (insertedTitles.has(s.title)) return;
    onSubtaskAdded && onSubtaskAdded({ title: s.title, duration_min: s.duration_min });
    setInsertedTitles((prev) => new Set(prev).add(s.title));
  };

  const insertAll = () => {
    subs.forEach((s) => {
      if (!insertedTitles.has(s.title)) {
        onSubtaskAdded && onSubtaskAdded({ title: s.title, duration_min: s.duration_min });
      }
    });
    setInsertedTitles(new Set(subs.map((s) => s.title)));
    toast.success(`Inserted ${subs.length} subtask${subs.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="border border-violet/30 bg-violet/5 p-2 space-y-2" data-testid="ai-helpers">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-violet shrink-0" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-violet flex-1">AI ASSIST</span>
        <button
          type="button"
          onClick={runEstimate}
          disabled={disabled || estimating}
          className={clsx(
            "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors",
            disabled
              ? "border-line text-ink-mute cursor-not-allowed"
              : "border-violet/40 text-violet hover:bg-violet/10"
          )}
          data-testid="ai-estimate-btn"
          title="Three-point AI duration estimate"
        >
          {estimating ? "ESTIMATING…" : "✦ ESTIMATE"}
        </button>
        <button
          type="button"
          onClick={runSuggest}
          disabled={disabled || suggesting}
          className={clsx(
            "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors",
            disabled
              ? "border-line text-ink-mute cursor-not-allowed"
              : "border-violet/40 text-violet hover:bg-violet/10"
          )}
          data-testid="ai-suggest-subtasks-btn"
          title="Suggest likely subtasks for this parent"
        >
          {suggesting ? "SUGGESTING…" : "✦ SUBTASKS"}
        </button>
      </div>

      {estimate && (
        <div className="border border-line bg-bg-elevated p-2 text-xs" data-testid="ai-estimate-result">
          <div className="flex items-center gap-2 mb-1">
            <span className="mono text-2xs uppercase tracking-widest text-ink-mute">RANGE</span>
            <span className="mono tabular-nums text-amber">
              {estimate.min_minutes}m – <strong>{estimate.likely_minutes}m</strong> – {estimate.max_minutes}m
            </span>
            <span
              className={clsx(
                "mono text-2xs px-1 ml-auto",
                estimate.confidence >= 70 ? "text-cyan" : estimate.confidence >= 40 ? "text-amber" : "text-ink-mute"
              )}
              title="Confidence (0–100)"
            >
              {estimate.confidence}%
            </span>
            <button
              type="button"
              onClick={applyEstimate}
              className="btn-primary px-2 py-0.5 mono text-2xs uppercase tracking-widest"
              data-testid="ai-estimate-apply"
            >
              APPLY {estimate.likely_minutes}M
            </button>
          </div>
          {estimate.rationale && (
            <div className="text-ink-dim text-2xs leading-snug">{estimate.rationale}</div>
          )}
        </div>
      )}

      {subs.length > 0 && (
        <div className="border border-line bg-bg-elevated p-2 space-y-1" data-testid="ai-suggested-subtasks">
          <div className="flex items-center gap-2 mb-1">
            <span className="mono text-2xs uppercase tracking-widest text-ink-mute">
              {subs.length} SUGGESTED
            </span>
            <button
              type="button"
              onClick={insertAll}
              className="btn-primary px-2 py-0.5 mono text-2xs uppercase tracking-widest ml-auto"
              data-testid="ai-suggest-insert-all"
            >
              INSERT ALL
            </button>
          </div>
          {subs.map((s, i) => {
            const inserted = insertedTitles.has(s.title);
            return (
              <div
                key={`${s.title}-${i}`}
                className={clsx(
                  "flex items-center gap-2 text-xs py-1 border-b border-line-subtle last:border-b-0",
                  inserted && "opacity-50"
                )}
                data-testid={`ai-suggest-row-${i}`}
              >
                <span className="mono text-2xs text-ink-mute tabular-nums w-12 shrink-0">
                  {s.duration_min}m
                </span>
                <span className="flex-1 min-w-0 truncate" title={s.rationale}>
                  {s.title}
                </span>
                <button
                  type="button"
                  onClick={() => insertSub(s)}
                  disabled={inserted}
                  className={clsx(
                    "shrink-0",
                    inserted ? "text-ink-mute cursor-not-allowed" : "text-violet hover:text-amber"
                  )}
                  title={inserted ? "Already inserted" : "Insert as subtask"}
                  data-testid={`ai-suggest-insert-${i}`}
                >
                  {inserted ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
